import {openDB} from 'idb';

const SOURCES_CACHE_DB_NAME = 'sources-cache';
const SOURCES_CACHE_DB_VERSION = 1;
const SOURCES_CACHE_STORE = 'views';
const SOURCES_CACHE_TTL_MS = 60 * 60 * 1000;

function getFolderIdForView(view) {
    if (view?.type !== 'folder') {
        return null;
    }

    const pathIds = Array.isArray(view.pathIds) ? view.pathIds : [];
    return pathIds[pathIds.length - 1] || view.id || null;
}

export function buildSourcesCacheKey(view) {
    if (!view || view.type === 'root') {
        return 'root';
    }

    if (view.type === 'preset') {
        return `preset:${view.id || view.branch || 'unknown'}`;
    }

    if (view.type === 'folder') {
        const folderId = getFolderIdForView(view);
        return `folder:${view.branch || 'root'}:${folderId || 'unknown'}`;
    }

    return 'root';
}

async function getSourcesCacheDb() {
    if (typeof window === 'undefined' || !window.indexedDB) {
        return null;
    }

    return await openDB(SOURCES_CACHE_DB_NAME, SOURCES_CACHE_DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(SOURCES_CACHE_STORE)) {
                db.createObjectStore(SOURCES_CACHE_STORE, {keyPath: 'key'});
            }
        },
    });
}

function isValidCacheEntry(entry) {
    return Boolean(entry)
        && typeof entry === 'object'
        && typeof entry.key === 'string'
        && Number.isFinite(entry.updatedAt)
        && Object.prototype.hasOwnProperty.call(entry, 'payload');
}

function isEntryExpired(entry) {
    return Date.now() - entry.updatedAt > SOURCES_CACHE_TTL_MS;
}

export async function readSourcesCache(view) {
    const db = await getSourcesCacheDb();
    if (!db) {
        return null;
    }

    const key = buildSourcesCacheKey(view);
    const entry = await db.get(SOURCES_CACHE_STORE, key);

    if (!isValidCacheEntry(entry)) {
        return null;
    }

    if (isEntryExpired(entry)) {
        await db.delete(SOURCES_CACHE_STORE, key);
        return null;
    }

    return entry;
}

export async function writeSourcesCache(view, payload) {
    const db = await getSourcesCacheDb();
    if (!db) {
        return null;
    }

    const entry = {
        key: buildSourcesCacheKey(view),
        payload,
        updatedAt: Date.now(),
    };

    await db.put(SOURCES_CACHE_STORE, entry);
    return entry;
}

export async function clearSourcesCache(view) {
    const db = await getSourcesCacheDb();
    if (!db) {
        return;
    }

    await db.delete(SOURCES_CACHE_STORE, buildSourcesCacheKey(view));
}
